import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { HelmetProvider } from "react-helmet-async";
import "react-toastify/dist/ReactToastify.css";

// Import Pages
import OnboardingPage from "./pages/OnboardingPage";
import ViewOrganizationProfilePage from "./pages/ViewOrganizationProfilePage";
import EditOrganizationProfilePage from "./pages/EditOrganizationProfilePage";
import PresentationViewPage from "./pages/PresentationViewPage";
import AuthPage from "./pages/AuthPage";
import HistoryPage from "./pages/HistoryPage";
import PresentationTypePage from "./pages/PresentationType";
import NewPresentationLandingPage from "./pages/NewPresentationLandingPage";
import PresentationShare from "./pages/PresentationShare";
import PitchDeckShare from "./components/pitchZynthShare/Share.jsx";
import PricingPage from "./pages/PricingPage";
import BlogPage from "./pages/BlogPage";
import ContactUsPage from "./pages/ContactUsPage";
import ReferPage from "./pages/ReferPage";

// Import Components
import ProtectedRoutes from "./components/shared/ProtectedRoutes";
import { SocketProvider } from "./components/payment/SubscriptionSocket";

// Import Context API
import { OutlineProvider } from "./components/presentation-view/OutlineContext";

// Import Google Analytics
import ReactGA from "react-ga";
import NewLandingPage from "./pages/NewLandingPage";
import UseCasesSalesPage from "./pages/UseCasesSalesPage";
import UseCasesProductPage from "./pages/UseCasesProductPage";
import UseCasesPitchPage from "./pages/UseCasesPitchPage";
import UseCasesMarketingPage from "./pages/UseCasesMarketingPage";
import UseCasesEmployeePage from "./pages/UseCasesEmloyeePage";
import UseCasesProjectPage from "./pages/UseCasesProjectPage";
import UseCasesBoardPage from "./pages/UseCasesBoardPage";
import UseCasesEducationPage from "./pages/UseCasesEducationPage";
import { PricingModalNew } from "./components/shared/PricingModalNew";
import Blog1 from "./components/blogs/Blog1";
import Blog2 from "./components/blogs/Blog2";
import Blog3 from "./components/blogs/Blog3";
import Blog4 from "./components/blogs/Blog4";
import Blog5 from "./components/blogs/Blog5";
import Blog7 from "./components/blogs/Blog7";
import Blog6 from "./components/blogs/Blog6";
import Blog8 from "./components/blogs/Blog8";
import Blog9 from "./components/blogs/Blog9";
import Blog10 from "./components/blogs/Blog10";
import ScrollToTop from "./utils/scrollToTop";
import ExtensionLandingPage from "./pages/ExtensionLandingPage";
import ApiPage from "./pages/ApiPage";
import Blog11 from "./components/blogs/Blog11";
import Blog12 from "./components/blogs/Blog12";
import Blog13 from "./components/blogs/Blog13";

import Invoice from "./pages/Invoice";
import StretchablePresentationPage from "./pages/StretchablePresentationPage";

import Blog14 from "./components/blogs/Blog14";
import Blog15 from "./components/blogs/Blog15";
import Blog16 from "./components/blogs/Blog16";
import NewPresentationLanding from "./pages/NewPresentationLanding";
import Blog17 from "./components/blogs/Blog17";
import Blog18 from "./components/blogs/Blog18";
import Blog19 from "./components/blogs/Blog19";
import Blog20 from "./components/blogs/Blog20";
import Blog21 from "./components/blogs/Blog21";
import Blog22 from "./components/blogs/Blog22";
import Blog23 from "./components/blogs/Blog23";
import Blog24 from "./components/blogs/Blog24";
import Blog25 from "./components/blogs/Blog25";
import Blog26 from "./components/blogs/Blog26";
import Blog27 from "./components/blogs/Blog27";
import Blog28 from "./components/blogs/Blog28";
import Blog29 from "./components/blogs/Blog29";
import Blog30 from "./components/blogs/Blog30";
import Blog31 from "./components/blogs/Blog31";
import Blog32 from "./components/blogs/Blog32";
import Blog33 from "./components/blogs/Blog33";
import Blog34 from "./components/blogs/Blog34";
import Blog35 from "./components/blogs/Blog35";
import Blog36 from "./components/blogs/Blog36";
import Blog37 from "./components/blogs/Blog37";
import Blog38 from "./components/blogs/Blog38";
import Blog39 from "./components/blogs/Blog39";
import Blog40 from "./components/blogs/Blog40";
import Blog41 from "./components/blogs/Blog41";
import Blog42 from "./components/blogs/Blog42";


// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Initialize Google Analytics
ReactGA.initialize("G-YHL4Z27NY0");

/**
 * Tracks Page Views for Google Analytics
 */
const TrackPageView: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.pageview(location.pathname + location.search);
  }, [location]);

  return null;
};

/**
 * ✅ Auth Token Expiry Check (Inside Router)
 * ✅ Excludes PresentationShare from auto logout
 * ✅ Fixes infinite redirect issue
 * ✅ Fixed timeout cleanup and memory leaks
 */
const AuthCheckComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === "/presentation-share") {
      return;
    }

    let logoutTimeout: NodeJS.Timeout | null = null;

    const handleLogout = () => {
      console.log("Logout triggered, clearing auth token");
      sessionStorage.removeItem("authToken");
      const excludedRoutes = [
        "/share",
        "/presentation-share",
        "/document/shared",
        "/pricing",
        "/",
        "/new-presentation",
        "/new-document",
        "/auth",
        "/white-label",
        "/#how-it-works",
        "/use-cases-sales",
        "/use-cases-product",
        "/use-cases-pitch",
        "/use-cases-marketing",
        "/use-cases-employee",
        "/use-cases-project",
        "/use-cases-board",
        "/use-cases-education",
        "/blog",
        "/contact-us",
        "/unlocking-the-journey-of-a-presentation-from-research-to-impact",
        "/struggling-to-create-the-perfect-sales-deck-zynths-ai-can-do-it-in-minutes",
        "/scaling-smart-how-ai-is-redefining-presentation-creation",
        "/the-hidden-cost-of-business-presentations-and-how-to-solve-it",
        "/pitching-to-impact-investors-strategies-for-communicating-social-and-environmental-impact",
        "/the-metrics-marathon-choosing-the-right-data-points-for-your-startup-pitch-deck",
        "/the-investors-perspective-what-they-look-for-in-a-pitch-deck",
        "/ai-powered-digital-transformation-the-competitive-edge-for-tech-companies",
        "/from-diy-to-ai-the-evolution-of-pitch-deck-creation",
        "/building-blocks-of-a-prefect-pitch-deck",
        "/from-manual-slides-to-ai-powered-decks",
        "/inside-zynth-ai",
        "/introducing-the-zynth-google-slides-add-on",
        "/zynth-api-powering-presentation-generation-for-developers-and-platforms",
        "/why-ai-will-replace-templates-in-presentation-building-the-future-of-slide-creation",
        "/how-to-instantly-generate-branded-presentations-without-a-designer",
        "/from-startup-pitches-to-corporate-reports-top-use-cases-for-zynth-ai",
        "/how-to-turn-text-into-google-slides-instantly-no-design-skills-required",
        "/how-to-build-custom-presentation-tools-on-top-of-zynths-api",
        "/apis-as-the-backbone-of-the-next-gen-productivity-tools",
        "/10-common-presentation-mistakes-and-how-ai-can-fix-them",
        "/scaling-content-production-with-zynth-a-guide-for-agencies-and-smes",
        "/step-by-step-installing-and-using-zynths-google-slides-add-on",
        "/automating-business-reporting-with-the-zynth-api",
        "/how-agencies-can-stay-ahead-with-ai-powered-content-and-design-tools",
        "/data-driven-storytelling-how-ai-turns-numbers-into-compelling-visuals",
        "/why-zynth-is-more-than-a-deck-builder-the-business-documentation-advantage",
        "/why-google-slides-zynth-is-a-game-changer-for-teams",
        "/for-saas-companies-embed-ai-powered-deck-creation-into-your-product",
        "/the-no-code-future-building-business-docs-without-designers-or-developers",
        "/from-bullet-points-to-visual-frameworks-the-evolution-of-slide-design",
        "/how-we-blend-text-graphics-to-create-engaging-presentations",
        "/top-productivity-hacks-using-zynths-google-slides-integration",
        "/introduction-scaling-enterprise-workflows-with-zynths-presentation-api",
        "/introduction-generative-ai-in-business-moving-beyond-text-to-visual-communication",
        "/zynth-for-startups-how-weve-helped-500-founders-build-investor-decks-instantly",
        "/free-credits-for-early-add-on-users-how-to-get-started-today",
        "/case-study-using-the-zynth-api-to-white-label-investor-deck-builders",
        "/infographics-vs-text-slides-choosing-the-right-format-for-your-message",
        "/enterprise-ready-presentations-white-labeling-zynth-for-your-business",
        "/the-future-of-business-documentation-apis-for-automated-presentations",
        "/extension",
        "/apiReference",
        "",
      ];
      const normalizedPath = location.pathname.replace(/\/+$/, "").toLowerCase();
      const isExcludedRoute = excludedRoutes.some(route =>
        normalizedPath === route.toLowerCase() ||
        normalizedPath.startsWith(route.toLowerCase() + "/")
      );
      if (!isExcludedRoute) {
        console.log("Redirecting to home page due to logout");
        window.location.href = "https://zynth.ai/";
      }
    };

    const checkTokenExpiry = () => {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        handleLogout();
        return;
      }
      try {
        const decoded: { exp?: number } = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        if (!decoded.exp || decoded.exp <= currentTime) {
          handleLogout();
          return;
        }
        // Clear any existing timeout before setting a new one
        if (logoutTimeout) {
          clearTimeout(logoutTimeout);
        }
        // Set auto logout timeout
        const timeoutDuration = (decoded.exp - currentTime) * 1000;
        console.log(`Auto logout scheduled in ${Math.floor(timeoutDuration / 1000)} seconds`);
        logoutTimeout = setTimeout(() => {
          console.log("Auto logout triggered due to token expiry");
          handleLogout();
        }, timeoutDuration);
      } catch (error) {
        console.error("Invalid token:", error);
        handleLogout();
      }
    };

    // Immediate check on mount/navigation
    checkTokenExpiry();

    // Also check every minute in case of clock drift or tab inactivity
    const interval = setInterval(checkTokenExpiry, 60 * 1000);

    // Also listen for visibility change (user comes back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkTokenExpiry();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      if (logoutTimeout) {
        clearTimeout(logoutTimeout);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [location, navigate]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    // ✅ Google Tag Manager Script
    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=AW-667504395";
    document.head.appendChild(gtagScript);

    gtagScript.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      window.gtag("config", "AW-667504395");
    };

    // ✅ Restore UTM & Referral Tracking Logic
    const currentUrl = window.location.href;
    const normalizedUrl = currentUrl.endsWith("/")
      ? currentUrl.slice(0, -1)
      : currentUrl;

    console.log("Normalized URL:", normalizedUrl);

    const storedUrl = localStorage.getItem("sign_up_link");
    if (!storedUrl || storedUrl !== normalizedUrl) {
      localStorage.setItem("sign_up_link", normalizedUrl);
    }

    // Parse UTM parameters
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = urlParams.toString();
    console.log("Extracted UTM Parameters:", utmParams);

    if (utmParams) {
      localStorage.setItem("utm_params", utmParams);
    }

    // Store referral details in sessionStorage
    const referralCode = urlParams.get("referralCode");
    const referredByOrgId = urlParams.get("orgId");
    const referredByUserId = urlParams.get("userId");

    if (referredByOrgId)
      sessionStorage.setItem("referredByOrgId", referredByOrgId);
    if (referredByUserId)
      sessionStorage.setItem("referredByUserId", referredByUserId);
    if (referralCode) sessionStorage.setItem("referralCode", referralCode);

    console.log("Referral Data Stored:", {
      referredByOrgId,
      referredByUserId,
      referralCode,
    });
  }, []);

  return (
    <HelmetProvider>
      <SocketProvider>
        <OutlineProvider>
          {" "}
          {/* ✅ Wrap App with Context Provider */}
          <Router>
            <AuthCheckComponent />
            <TrackPageView />
            <ScrollToTop />
            <Routes>
            {/* Public Routes */}
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    window.location.hostname === "documents.zynth.ai"
                      ? "/new-document"
                      : "/new-presentation"
                  }
                  replace
                />
              }
            />
            <Route path="/white-label" element={<NewLandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route path="/use-cases-sales" element={<UseCasesSalesPage />} />
            <Route
              path="/use-cases-product"
              element={<UseCasesProductPage />}
            />
            <Route path="/use-cases-pitch" element={<UseCasesPitchPage />} />
            <Route
              path="/use-cases-marketing"
              element={<UseCasesMarketingPage />}
            />
            <Route
              path="/use-cases-employee"
              element={<UseCasesEmployeePage />}
            />
            <Route
              path="/use-cases-project"
              element={<UseCasesProjectPage />}
            />
            <Route path="/use-cases-board" element={<UseCasesBoardPage />} />
            <Route
              path="/use-cases-education"
              element={<UseCasesEducationPage />}
            />

            <Route
              path="/unlocking-the-journey-of-a-presentation-from-research-to-impact"
              element={<Blog1 />}
            />
            <Route
              path="/struggling-to-create-the-perfect-sales-deck-zynths-ai-can-do-it-in-minutes"
              element={<Blog2 />}
            />
            <Route
              path="/scaling-smart-how-ai-is-redefining-presentation-creation"
              element={<Blog3 />}
            />
            <Route
              path="/the-hidden-cost-of-business-presentations-and-how-to-solve-it"
              element={<Blog4 />}
            />
            <Route
              path="/pitching-to-impact-investors-strategies-for-communicating-social-and-environmental-impact"
              element={<Blog5 />}
            />
            <Route
              path="/the-metrics-marathon-choosing-the-right-data-points-for-your-startup-pitch-deck"
              element={<Blog6 />}
            />
            <Route
              path="/the-investors-perspective-what-they-look-for-in-a-pitch-deck"
              element={<Blog7 />}
            />
            <Route
              path="/ai-powered-digital-transformation-the-competitive-edge-for-tech-companies"
              element={<Blog8 />}
            />
            <Route
              path="/from-diy-to-ai-the-evolution-of-pitch-deck-creation"
              element={<Blog9 />}
            />
            <Route
              path="/building-blocks-of-a-prefect-pitch-deck"
              element={<Blog10 />}
            />
            <Route
              path="/from-manual-slides-to-ai-powered-decks"
              element={<Blog11 />}
            />
            <Route path="/inside-zynth-ai" element={<Blog12 />} />
            <Route
              path="/introducing-the-zynth-google-slides-add-on"
              element={<Blog13 />}
            />

            <Route
              path="/zynth-api-powering-presentation-generation-for-developers-and-platforms"
              element={<Blog14 />}
            />
            <Route
              path="/why-ai-will-replace-templates-in-presentation-building-the-future-of-slide-creation"
              element={<Blog15 />}
            />
            <Route
              path="/how-to-instantly-generate-branded-presentations-without-a-designer"
              element={<Blog16 />}
            />
            <Route
              path="/from-startup-pitches-to-corporate-reports-top-use-cases-for-zynth-ai"
              element={<Blog17 />}
            />
            <Route
              path="/how-to-turn-text-into-google-slides-instantly-no-design-skills-required"
              element={<Blog18 />}
            />
            <Route
              path="/how-to-build-custom-presentation-tools-on-top-of-zynths-api"
              element={<Blog19 />}
            />
            <Route
              path="/apis-as-the-backbone-of-the-next-gen-productivity-tools"
              element={<Blog20 />}
            />
            <Route
              path="/10-common-presentation-mistakes-and-how-ai-can-fix-them"
              element={<Blog21 />}
            />
            <Route
              path="/scaling-content-production-with-zynth-a-guide-for-agencies-and-smes"
              element={<Blog22 />}
            />
            <Route
              path="/step-by-step-installing-and-using-zynths-google-slides-add-on"
              element={<Blog23 />}
            />
            <Route
              path="/automating-business-reporting-with-the-zynth-api"
              element={<Blog24 />}
            />
            <Route
              path="/how-agencies-can-stay-ahead-with-ai-powered-content-and-design-tools"
              element={<Blog25 />}
            />
            <Route
              path="/data-driven-storytelling-how-ai-turns-numbers-into-compelling-visuals"
              element={<Blog26 />}
            />
            <Route
              path="/why-zynth-is-more-than-a-deck-builder-the-business-documentation-advantage"
              element={<Blog27 />}
            />
            <Route
              path="/why-google-slides-zynth-is-a-game-changer-for-teams"
              element={<Blog28 />}
            />
            <Route
              path="/for-saas-companies-embed-ai-powered-deck-creation-into-your-product"
              element={<Blog29 />}
            />
            <Route
              path="/the-no-code-future-building-business-docs-without-designers-or-developers"
              element={<Blog30 />}
            />
            <Route
              path="/from-bullet-points-to-visual-frameworks-the-evolution-of-slide-design"
              element={<Blog31 />}
            />
            <Route
              path="/how-we-blend-text-graphics-to-create-engaging-presentations"
              element={<Blog32 />}
            />
          <Route
              path="/top-productivity-hacks-using-zynths-google-slides-integration"
              element={<Blog33 />}
            />
          <Route
              path="/introduction-scaling-enterprise-workflows-with-zynths-presentation-api"
              element={<Blog34 />}
            />
          <Route
              path="/introduction-generative-ai-in-business-moving-beyond-text-to-visual-communication"
              element={<Blog35 />}
            />
            <Route
              path="/why-speed-matters-saving-hours-with-ai-presentations"
              element={<Blog36 />}
            />
            <Route
  path="/zynth-for-startups-how-weve-helped-500-founders-build-investor-decks-instantly"
  element={<Blog37 />}
/>

<Route
  path="/free-credits-for-early-add-on-users-how-to-get-started-today"
  element={<Blog38 />}
/>

<Route
  path="/case-study-using-the-zynth-api-to-white-label-investor-deck-builders"
  element={<Blog39 />}
/>

<Route
  path="/infographics-vs-text-slides-choosing-the-right-format-for-your-message"
  element={<Blog40 />}
/>

<Route
  path="/enterprise-ready-presentations-white-labeling-zynth-for-your-business"
  element={<Blog41 />}
/>

<Route
  path="/the-future-of-business-documentation-apis-for-automated-presentations"
  element={<Blog42 />}
/>



            <Route path="/extension" element={<ExtensionLandingPage />} />
            <Route path="/apiReference" element={<ApiPage />} />
            <Route
              path="/new-presentation"
              element={<NewPresentationLanding />}
            />
            <Route
              path="/new-document"
              element={<NewPresentationLandingPage />}
            />

            {/* Sharing Routes */}
            <Route path="/presentation-share" element={<PresentationShare />} />
            <Route path="/share" element={<PitchDeckShare />} />
            <Route path="/document/shared/:shareId" element={<StretchablePresentationPage />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoutes />}>
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route
                path="/organization-profile"
                element={<ViewOrganizationProfilePage />}
              />
              <Route
                path="/edit-organization-profile"
                element={<EditOrganizationProfilePage />}
              />
              {/* <Route
                path="/new-presentation"
                element={<PresentationTypePage />}
              /> */}
              <Route
                path="/presentation-view"
                element={<PresentationViewPage />}
              />
              <Route
                path="/documentview"
                element={<StretchablePresentationPage />}
              />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/refer" element={<ReferPage />} />
              <Route path="/invoice" element={<Invoice />} />
            </Route>
          </Routes>
        </Router>
        <ToastContainer />
      </OutlineProvider>
    </SocketProvider>
    </HelmetProvider>
  );
};

export default App;
